import { ChangeDetectorRef, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DriveContextMenuCoordinator } from './drive-context-menu.coordinator';
import { DriveMoveExecutionCoordinator } from './drive-move-execution.coordinator';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveItem } from './drive-viewer.models';
import { DriveMoveState } from './drive-viewer.state';

export class DriveMoveDialogCoordinator {
  constructor(
    private readonly state: DriveMoveState,
    private readonly selection: DriveSelectionQuery,
    private readonly execution: DriveMoveExecutionCoordinator,
    private readonly contextMenu: DriveContextMenuCoordinator,
    private readonly dialog: MatDialog,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly translate: (key: string) => string
  ) {}

  toggleTargets(): void { this.state.contextMoveExpanded = !this.state.contextMoveExpanded; }

  open(template: TemplateRef<unknown>): void {
    const files = this.contextFiles();
    if (!files.length) {
      this.contextMenu.close();
      return;
    }
    this.state.moveDialogFiles = files;
    this.state.moveDialogSearch = '';
    this.state.moveDialogBusy = false;
    this.state.moveDialogError = '';
    this.contextMenu.close();
    this.state.moveDialogRef = this.dialog.open(template, {
      width: '520px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      panelClass: 'drive-move-dialog-panel'
    });
    this.changeDetector.detectChanges();
    this.state.moveDialogRef.afterClosed().subscribe(() => this.reset());
  }

  filesLabel(): string {
    return this.state.moveDialogFiles.length === 1
      ? this.state.moveDialogFiles[0].name
      : `${this.state.moveDialogFiles.length} ${this.translate('drive.move_dialog.items_suffix')}`;
  }

  createFolder(): void {
    this.state.moveDialogRef?.close();
    this.contextMenu.openCreateFolderFromFallback();
  }

  moveToFolder(folder: DriveItem): void {
    if (this.state.moveDialogBusy) return;
    this.state.moveDialogBusy = true;
    this.state.moveDialogError = '';
    const started = this.execution.moveFiles(this.state.moveDialogFiles, folder, true, false, {
      success: () => {
        this.state.moveDialogBusy = false;
        this.state.moveDialogRef?.close();
      },
      error: () => {
        this.state.moveDialogBusy = false;
        this.state.moveDialogError = this.translate('drive.move_dialog.error');
      }
    });
    if (!started) {
      this.state.moveDialogBusy = false;
      this.state.moveDialogError = this.translate('drive.move_dialog.invalid_target');
    }
  }

  moveContextFilesToFolder(folder: DriveItem): void {
    const files = this.contextFiles();
    if (!files.length) {
      this.contextMenu.close();
      return;
    }
    if (this.execution.moveFiles(files, folder, true)) this.contextMenu.close();
  }

  contextFiles(): DriveItem[] {
    const item = this.state.contextMenuItem;
    if (!item || item.type === 'folder') return [];
    const selected = this.selection.selectedFiles();
    return this.selection.isSelected(item.id) && selected.length ? selected : [item];
  }

  private reset(): void {
    this.state.moveDialogFiles = [];
    this.state.moveDialogSearch = '';
    this.state.moveDialogBusy = false;
    this.state.moveDialogError = '';
    this.state.moveDialogRef = undefined;
  }
}
